import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface FaqPair {
  question: string;
  answer: string;
}

export const EMPTY_FAQ: FaqPair[] = [{ question: "", answer: "" }];

const HEADING_RE = /^##\s+(.+?)\s*$/;

/** Parse an existing markdown body into Q&A pairs. The Rails-side
 * `Documents::FaqParser` uses the same rule (H2 = question, content
 * between H2s = answer), so this round-trips losslessly with a doc
 * authored via the canonical form.
 *
 * Returns `null` if the body has no H2 headings — the caller then
 * keeps the raw textarea so an FAQ-mode edit doesn't clobber pre-
 * existing free-form markdown. */
export function parseFaqMarkdown(body: string): FaqPair[] | null {
  if (!body) return null;
  const lines = body.split(/\r?\n/);
  const items: FaqPair[] = [];
  let current: FaqPair | null = null;
  for (const line of lines) {
    const m = line.match(HEADING_RE);
    if (m) {
      if (current) items.push(current);
      current = { question: (m[1] ?? "").trim(), answer: "" };
    } else if (current) {
      current.answer += line + "\n";
    }
  }
  if (current) items.push(current);
  if (items.length === 0) return null;
  return items.map((it) => ({ question: it.question, answer: it.answer.trim() }));
}

/** Serialise Q&A rows back to the canonical markdown body. Each pair
 * gets a single H2 + a blank line + the answer + a blank line. Empty
 * pairs are dropped so a trailing add-row doesn't produce a phantom
 * "What is this?" / blank-answer item on the customer side. */
export function serializeFaqMarkdown(items: FaqPair[]): string {
  return items
    .map((it) => ({ question: it.question.trim(), answer: it.answer.trim() }))
    .filter((it) => it.question.length > 0 || it.answer.length > 0)
    .map((it) => `## ${it.question}\n\n${it.answer}\n`)
    .join("\n");
}

export function FaqFieldsEditor({
  value, onChange,
}: { value: FaqPair[]; onChange: (next: FaqPair[]) => void }) {
  const updatePair = (idx: number, patch: Partial<FaqPair>) =>
    onChange(value.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const addPair = () => onChange([...value, { question: "", answer: "" }]);
  const removePair = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
            FAQ
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
            Each row becomes one expandable Q&amp;A on the customer
            support portal. Answers accept Markdown.
          </Typography>
        </Box>
        <Button type="button" variant="text" color="inherit" size="small" onClick={addPair}>
          + Add question
        </Button>
      </Box>

      <Box component="ol" sx={{ display: "grid", gap: 1.5, listStyle: "none", m: 0, p: 0 }}>
        {value.map((pair, idx) => (
          <Box
            component="li"
            key={idx}
            sx={{
              display: "grid",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.default",
              p: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                component="span"
                sx={{
                  display: "grid",
                  height: 24,
                  width: 24,
                  placeItems: "center",
                  borderRadius: "50%",
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                  fontSize: 11,
                  fontFamily: "ui-monospace, monospace",
                  color: "primary.main",
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </Box>
              <TextField
                id={`faq-q-${idx}`}
                placeholder="Question"
                value={pair.question}
                onChange={(e) => updatePair(idx, { question: e.target.value })}
                inputProps={{ "aria-label": `Question ${idx + 1}` }}
                fullWidth
                sx={{ "& .MuiInputBase-input": { fontWeight: 500 } }}
              />
              <IconButton
                type="button"
                size="small"
                onClick={() => removePair(idx)}
                aria-label={`Remove question ${idx + 1}`}
                disabled={value.length === 1}
                sx={{ color: "text.disabled" }}
              >
                ×
              </IconButton>
            </Box>
            <TextField
              id={`faq-a-${idx}`}
              multiline
              rows={4}
              placeholder="Answer (Markdown supported — lists, links, **bold**…)"
              value={pair.answer}
              onChange={(e) => updatePair(idx, { answer: e.target.value })}
              inputProps={{ "aria-label": `Answer ${idx + 1}` }}
              fullWidth
              sx={{ "& .MuiInputBase-input": { fontFamily: "ui-monospace, monospace", fontSize: 12 } }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
